import { appendPaginationQuery } from '../../../types/pagination';
import type { ApiClient } from '../client';
import {
  LOGIN_VISIT_PATHS,
  SCHOOL_API_PATHS,
  SUPER_ADMIN_PATHS,
  TENANT_PATHS,
} from '../../../config/api-paths';
import type {
  ApiResponse,
  BackfillTenantSlugsResponse,
  CreateSuperAdminTenantRequest,
  CreateSuperAdminTenantResponse,
  ListSuperAdminTenantsQuery,
  ListSuperAdminTenantsResponse,
  SetSuperAdminTenantSlugRequest,
  SetSuperAdminTenantSlugResponse,
  RecordTenantPaymentRequest,
  RecordTenantPaymentResponse,
  ResetSuperAdminTenantOwnerPasswordRequest,
  ResetSuperAdminTenantOwnerPasswordResponse,
  ResendSuperAdminTenantOwnerWelcomeRequest,
  ResendSuperAdminTenantOwnerWelcomeResponse,
  TransferSuperAdminTenantOwnershipRequest,
  TransferSuperAdminTenantOwnershipResponse,
  DeactivateSuperAdminTenantOwnerResponse,
  CreateGlobalExpenseCategoryResponse,
  CreateGlobalFeeTemplateResponse,
  GlobalExpenseCategory,
  GlobalFeeTemplateSummary,
  ListGlobalExpenseCategoriesResponse,
  ImpersonateSuperAdminTenantOwnerResponse,
  ListGlobalFeeTemplatesResponse,
  SuperAdminTenantOwnerResponse,
  ListPlatformTenantAuditsQuery,
  ListPlatformTenantAuditsResponse,
  LoginPageVisitCountResponse,
  ListLoginPageVisitsQuery,
  ListLoginPageVisitsResponse,
  PlatformCommunicationUsageQuery,
  PlatformCommunicationUsageResponse,
  RecordLoginPageVisitRequest,
  RecordLoginPageVisitResponse,
  SaveTenantDashboardLayoutRequest,
  SaveGlobalExpenseCategoryRequest,
  SaveGlobalFeeTemplateRequest,
  PlatformDunningConfig,
  RunPlatformDunningResponse,
  SuperAdminBillingDashboardResponse,
  SuperAdminLoginRequest,
  SuperAdminLoginResponse,
  SuperAdminTenantCommunicationConfig,
  SuperAdminTenantPaytmSettings,
  SuperAdminTenantModulesResponse,
  ListTenantModuleSubscriptionsResponse,
  TenantDashboardLayoutCatalogResponse,
  UpdatePlatformDunningConfigRequest,
  UpdateTenantBillingRequest,
  UpdateTenantBillingResponse,
  UpdateTenantDunningExemptRequest,
  UpdateTenantDunningExemptResponse,
  UpdateSuperAdminTenantModulesRequest,
  UpdateSuperAdminTenantProductOfferingRequest,
  UpdateSuperAdminTenantProductOfferingResponse,
  UpdateSuperAdminTenantStudentResidenceModeRequest,
  UpdateSuperAdminTenantStudentResidenceModeResponse,
  UpdateSuperAdminTenantCommunicationConfigRequest,
  UpdateSuperAdminTenantPaytmSettingsRequest,
  UpdateSuperAdminTenantRequest,
} from '../types';
import type { ChangePasswordRequest, ChangePasswordResponse } from '../types/auth';
import type { SchoolProfileJson, UpdateSchoolProfileRequest } from '../types/teacher-portal';
import { persistentLogoUrlForSave } from '../../../utils/tenantMediaUpload';

/** Map super-admin tenant profile fields to school profile PUT body. */
function schoolProfileBodyFromTenantUpdate(
  body: UpdateSuperAdminTenantRequest
): UpdateSchoolProfileRequest {
  return {
    name: body.name,
    org_code: body.org_code,
    org_type: body.org_type,
    status: body.status,
    board_code: body.board_code,
    country_code: body.country_code,
    currency_code: body.currency_code,
    logo_url: persistentLogoUrlForSave(body.logo_url, body.logo_media_id),
    logo_media_id: body.logo_media_id,
    affiliation_board: body.affiliation_board,
    affiliation_no: body.affiliation_no,
    affiliated_for: body.affiliated_for,
    affiliation_category: body.affiliation_category,
    affiliation_valid_until: body.affiliation_valid_until,
    student_residence_mode: body.student_residence_mode ?? undefined,
  };
}

export function bindSuperAdmin(client: ApiClient) {
  return {
    async loginSuperAdmin(body: SuperAdminLoginRequest): Promise<ApiResponse<SuperAdminLoginResponse>> {
      return client.request<SuperAdminLoginResponse>(SUPER_ADMIN_PATHS.login, {
        method: 'POST',
        body: JSON.stringify(body),
        skipAuth: true,
      });
    },

    async changeSuperAdminPassword(
      body: ChangePasswordRequest
    ): Promise<ApiResponse<ChangePasswordResponse>> {
      return client.request<ChangePasswordResponse>(SUPER_ADMIN_PATHS.passwordChange, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async listSuperAdminTenants(
      params?: ListSuperAdminTenantsQuery
    ): Promise<ApiResponse<ListSuperAdminTenantsResponse>> {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      if (params?.search?.trim()) {
        qs.append('search', params.search.trim());
      }
      const query = qs.toString();
      const path = query ? `${SUPER_ADMIN_PATHS.tenants}?${query}` : SUPER_ADMIN_PATHS.tenants;
      return client.request<ListSuperAdminTenantsResponse>(path);
    },

    async listGlobalFeeTemplates(): Promise<ApiResponse<ListGlobalFeeTemplatesResponse>> {
      return client.request<ListGlobalFeeTemplatesResponse>(SUPER_ADMIN_PATHS.feeTemplates);
    },

    async createGlobalFeeTemplate(
      body: SaveGlobalFeeTemplateRequest
    ): Promise<ApiResponse<CreateGlobalFeeTemplateResponse>> {
      return client.request<CreateGlobalFeeTemplateResponse>(SUPER_ADMIN_PATHS.feeTemplates, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async updateGlobalFeeTemplate(
      templateId: string,
      body: SaveGlobalFeeTemplateRequest
    ): Promise<ApiResponse<GlobalFeeTemplateSummary>> {
      return client.request<GlobalFeeTemplateSummary>(
        SUPER_ADMIN_PATHS.feeTemplate(templateId),
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async deleteGlobalFeeTemplate(templateId: string): Promise<ApiResponse<void>> {
      return client.request<void>(SUPER_ADMIN_PATHS.feeTemplate(templateId), {
        method: 'DELETE',
      });
    },

    async listGlobalExpenseCategories(): Promise<ApiResponse<ListGlobalExpenseCategoriesResponse>> {
      return client.request<ListGlobalExpenseCategoriesResponse>(SUPER_ADMIN_PATHS.expenseCategories);
    },

    async createGlobalExpenseCategory(
      body: SaveGlobalExpenseCategoryRequest
    ): Promise<ApiResponse<CreateGlobalExpenseCategoryResponse>> {
      return client.request<CreateGlobalExpenseCategoryResponse>(SUPER_ADMIN_PATHS.expenseCategories, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async updateGlobalExpenseCategory(
      categoryId: string,
      body: SaveGlobalExpenseCategoryRequest
    ): Promise<ApiResponse<GlobalExpenseCategory>> {
      return client.request<GlobalExpenseCategory>(SUPER_ADMIN_PATHS.expenseCategory(categoryId), {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async deleteGlobalExpenseCategory(categoryId: string): Promise<ApiResponse<void>> {
      return client.request<void>(SUPER_ADMIN_PATHS.expenseCategory(categoryId), {
        method: 'DELETE',
      });
    },

    async getSuperAdminBillingDashboard(): Promise<ApiResponse<SuperAdminBillingDashboardResponse>> {
      return client.request<SuperAdminBillingDashboardResponse>(SUPER_ADMIN_PATHS.billing);
    },

    async getPlatformDunningConfig(): Promise<ApiResponse<PlatformDunningConfig>> {
      return client.request<PlatformDunningConfig>(SUPER_ADMIN_PATHS.billingDunningConfig);
    },

    async updatePlatformDunningConfig(
      body: UpdatePlatformDunningConfigRequest
    ): Promise<ApiResponse<PlatformDunningConfig>> {
      return client.request<PlatformDunningConfig>(SUPER_ADMIN_PATHS.billingDunningConfig, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    async runPlatformDunningNow(): Promise<ApiResponse<RunPlatformDunningResponse>> {
      return client.request<RunPlatformDunningResponse>(SUPER_ADMIN_PATHS.billingDunningRun, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },

    async updateSuperAdminTenantDunningExempt(
      tenantId: string | number,
      body: UpdateTenantDunningExemptRequest
    ): Promise<ApiResponse<UpdateTenantDunningExemptResponse>> {
      return client.request<UpdateTenantDunningExemptResponse>(
        SUPER_ADMIN_PATHS.tenantBillingDunningExempt(tenantId),
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async recordLoginPageVisit(
      body?: RecordLoginPageVisitRequest
    ): Promise<ApiResponse<RecordLoginPageVisitResponse>> {
      return client.request<RecordLoginPageVisitResponse>(LOGIN_VISIT_PATHS.record, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
        skipAuth: true,
      });
    },

    async getLoginPageVisitCount(): Promise<ApiResponse<LoginPageVisitCountResponse>> {
      return client.request<LoginPageVisitCountResponse>(LOGIN_VISIT_PATHS.count, {
        skipAuth: true,
      });
    },

    async listSuperAdminLoginVisits(
      params?: ListLoginPageVisitsQuery
    ): Promise<ApiResponse<ListLoginPageVisitsResponse>> {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      const query = qs.toString();
      const path = query
        ? `${SUPER_ADMIN_PATHS.loginVisits}?${query}`
        : SUPER_ADMIN_PATHS.loginVisits;
      return client.request<ListLoginPageVisitsResponse>(path);
    },

    async listTenantModuleSubscriptions(): Promise<ApiResponse<ListTenantModuleSubscriptionsResponse>> {
      return client.request<ListTenantModuleSubscriptionsResponse>(TENANT_PATHS.subscriptionsModules);
    },

    async createSuperAdminTenant(
      body: CreateSuperAdminTenantRequest
    ): Promise<ApiResponse<CreateSuperAdminTenantResponse>> {
      return client.request<CreateSuperAdminTenantResponse>(SUPER_ADMIN_PATHS.tenants, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async setSuperAdminTenantSlug(
      tenantId: string | number,
      body: SetSuperAdminTenantSlugRequest
    ): Promise<ApiResponse<SetSuperAdminTenantSlugResponse>> {
      return client.request<SetSuperAdminTenantSlugResponse>(
        SUPER_ADMIN_PATHS.tenantSlug(tenantId),
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async backfillSuperAdminTenantSlugs(): Promise<ApiResponse<BackfillTenantSlugsResponse>> {
      return client.request<BackfillTenantSlugsResponse>(SUPER_ADMIN_PATHS.backfillSlugs, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },

    /** Backend OpenAPI: tenant profile updates use `PUT /api/school` scoped by `x-tenant-id`. */
    async updateSuperAdminTenant(
      tenantId: string | number,
      body: UpdateSuperAdminTenantRequest
    ): Promise<ApiResponse<SchoolProfileJson>> {
      return client.request<SchoolProfileJson>(SCHOOL_API_PATHS.profile, {
        method: 'PUT',
        body: JSON.stringify(schoolProfileBodyFromTenantUpdate(body)),
        tenantId,
        suppressSessionExpiry: true,
      });
    },

    /** Load full school profile (logo, affiliation) for a tenant. */
    async getSuperAdminTenantProfile(
      tenantId: string | number
    ): Promise<ApiResponse<SchoolProfileJson>> {
      return client.request<SchoolProfileJson>(SCHOOL_API_PATHS.profile, {
        tenantId,
        suppressSessionExpiry: true,
      });
    },

    async getSuperAdminTenantModules(
      tenantId: string | number
    ): Promise<ApiResponse<SuperAdminTenantModulesResponse>> {
      return client.request<SuperAdminTenantModulesResponse>(
        SUPER_ADMIN_PATHS.tenantModules(tenantId)
      );
    },

    async updateSuperAdminTenantModules(
      tenantId: string | number,
      body: UpdateSuperAdminTenantModulesRequest
    ): Promise<ApiResponse<SuperAdminTenantModulesResponse>> {
      return client.request<SuperAdminTenantModulesResponse>(
        SUPER_ADMIN_PATHS.tenantModules(tenantId),
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async updateSuperAdminTenantProductOffering(
      tenantId: string | number,
      body: UpdateSuperAdminTenantProductOfferingRequest
    ): Promise<ApiResponse<UpdateSuperAdminTenantProductOfferingResponse>> {
      return client.request<UpdateSuperAdminTenantProductOfferingResponse>(
        SUPER_ADMIN_PATHS.tenantProductOffering(tenantId),
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async updateSuperAdminTenantStudentResidenceMode(
      tenantId: string | number,
      body: UpdateSuperAdminTenantStudentResidenceModeRequest
    ): Promise<ApiResponse<UpdateSuperAdminTenantStudentResidenceModeResponse>> {
      return client.request<UpdateSuperAdminTenantStudentResidenceModeResponse>(
        SUPER_ADMIN_PATHS.tenantStudentResidenceMode(tenantId),
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async updateSuperAdminTenantBilling(
      tenantId: string | number,
      body: UpdateTenantBillingRequest
    ): Promise<ApiResponse<UpdateTenantBillingResponse>> {
      return client.request<UpdateTenantBillingResponse>(
        SUPER_ADMIN_PATHS.tenantBilling(tenantId),
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async recordSuperAdminTenantPayment(
      tenantId: string | number,
      body: RecordTenantPaymentRequest
    ): Promise<ApiResponse<RecordTenantPaymentResponse>> {
      return client.request<RecordTenantPaymentResponse>(
        SUPER_ADMIN_PATHS.tenantPayments(tenantId),
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      );
    },

    async getSuperAdminTenantOwner(
      tenantId: string | number
    ): Promise<ApiResponse<SuperAdminTenantOwnerResponse>> {
      return client.request<SuperAdminTenantOwnerResponse>(SUPER_ADMIN_PATHS.tenantOwner(tenantId));
    },

    async resetSuperAdminTenantOwnerPassword(
      tenantId: string | number,
      body: ResetSuperAdminTenantOwnerPasswordRequest = {}
    ): Promise<ApiResponse<ResetSuperAdminTenantOwnerPasswordResponse>> {
      return client.request<ResetSuperAdminTenantOwnerPasswordResponse>(
        SUPER_ADMIN_PATHS.tenantOwnerResetPassword(tenantId),
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      );
    },

    async resendSuperAdminTenantOwnerWelcome(
      tenantId: string | number,
      body: ResendSuperAdminTenantOwnerWelcomeRequest = {}
    ): Promise<ApiResponse<ResendSuperAdminTenantOwnerWelcomeResponse>> {
      return client.request<ResendSuperAdminTenantOwnerWelcomeResponse>(
        SUPER_ADMIN_PATHS.tenantOwnerResendWelcome(tenantId),
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      );
    },

    async transferSuperAdminTenantOwnership(
      tenantId: string | number,
      body: TransferSuperAdminTenantOwnershipRequest
    ): Promise<ApiResponse<TransferSuperAdminTenantOwnershipResponse>> {
      return client.request<TransferSuperAdminTenantOwnershipResponse>(
        SUPER_ADMIN_PATHS.tenantOwnerTransfer(tenantId),
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      );
    },

    async deactivateSuperAdminTenantOwner(
      tenantId: string | number
    ): Promise<ApiResponse<DeactivateSuperAdminTenantOwnerResponse>> {
      return client.request<DeactivateSuperAdminTenantOwnerResponse>(
        SUPER_ADMIN_PATHS.tenantOwnerDeactivate(tenantId),
        {
          method: 'POST',
          body: JSON.stringify({}),
        }
      );
    },

    async impersonateSuperAdminTenantOwner(
      tenantId: string | number
    ): Promise<ApiResponse<ImpersonateSuperAdminTenantOwnerResponse>> {
      return client.request<ImpersonateSuperAdminTenantOwnerResponse>(
        SUPER_ADMIN_PATHS.tenantImpersonate(tenantId),
        {
          method: 'POST',
          body: JSON.stringify({}),
        }
      );
    },

    async listSuperAdminTenantAudits(
      tenantId: string | number,
      params?: ListPlatformTenantAuditsQuery
    ): Promise<ApiResponse<ListPlatformTenantAuditsResponse>> {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      if (params?.action?.trim()) {
        qs.append('action', params.action.trim());
      }
      const query = qs.toString();
      const path = query
        ? `${SUPER_ADMIN_PATHS.tenantAudits(tenantId)}?${query}`
        : SUPER_ADMIN_PATHS.tenantAudits(tenantId);
      return client.request<ListPlatformTenantAuditsResponse>(path);
    },

    async getSuperAdminTenantDashboardLayoutCatalog(
      tenantId: string | number,
      dashboardRole: string
    ): Promise<ApiResponse<TenantDashboardLayoutCatalogResponse>> {
      const query = new URLSearchParams({ dashboard_role: dashboardRole });
      return client.request<TenantDashboardLayoutCatalogResponse>(
        `${SUPER_ADMIN_PATHS.tenantDashboardLayoutCatalog(tenantId)}?${query}`
      );
    },

    async saveSuperAdminTenantDashboardLayout(
      tenantId: string | number,
      body: SaveTenantDashboardLayoutRequest
    ): Promise<ApiResponse<TenantDashboardLayoutCatalogResponse>> {
      return client.request<TenantDashboardLayoutCatalogResponse>(
        SUPER_ADMIN_PATHS.tenantDashboardLayout(tenantId),
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async resetSuperAdminTenantDashboardLayout(
      tenantId: string | number,
      dashboardRole: string
    ): Promise<ApiResponse<TenantDashboardLayoutCatalogResponse>> {
      const query = new URLSearchParams({ dashboard_role: dashboardRole });
      return client.request<TenantDashboardLayoutCatalogResponse>(
        `${SUPER_ADMIN_PATHS.tenantDashboardLayout(tenantId)}?${query}`,
        { method: 'DELETE' }
      );
    },

    async getSuperAdminTenantCommunicationConfig(
      tenantId: string | number
    ): Promise<ApiResponse<SuperAdminTenantCommunicationConfig>> {
      return client.request<SuperAdminTenantCommunicationConfig>(
        SUPER_ADMIN_PATHS.tenantCommunicationConfig(tenantId)
      );
    },

    async updateSuperAdminTenantCommunicationConfig(
      tenantId: string | number,
      body: UpdateSuperAdminTenantCommunicationConfigRequest
    ): Promise<ApiResponse<SuperAdminTenantCommunicationConfig>> {
      return client.request<SuperAdminTenantCommunicationConfig>(
        SUPER_ADMIN_PATHS.tenantCommunicationConfig(tenantId),
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async getSuperAdminTenantPaytmSettings(
      tenantId: string | number
    ): Promise<ApiResponse<SuperAdminTenantPaytmSettings>> {
      return client.request<SuperAdminTenantPaytmSettings>(
        SUPER_ADMIN_PATHS.tenantPaytmSettings(tenantId)
      );
    },

    async updateSuperAdminTenantPaytmSettings(
      tenantId: string | number,
      body: UpdateSuperAdminTenantPaytmSettingsRequest
    ): Promise<ApiResponse<SuperAdminTenantPaytmSettings>> {
      return client.request<SuperAdminTenantPaytmSettings>(
        SUPER_ADMIN_PATHS.tenantPaytmSettings(tenantId),
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async getSuperAdminCommunicationUsage(
      params?: PlatformCommunicationUsageQuery
    ): Promise<ApiResponse<PlatformCommunicationUsageResponse>> {
      const qs = new URLSearchParams();
      if (params?.tenant_id != null && String(params.tenant_id).trim()) {
        qs.set('tenant_id', String(params.tenant_id));
      }
      if (params?.granularity) qs.set('granularity', params.granularity);
      if (params?.from_date) qs.set('from_date', params.from_date);
      if (params?.to_date) qs.set('to_date', params.to_date);
      if (params?.channel) qs.set('channel', params.channel);
      const query = qs.toString();
      const path = query
        ? `${SUPER_ADMIN_PATHS.communicationUsage}?${query}`
        : SUPER_ADMIN_PATHS.communicationUsage;
      return client.request<PlatformCommunicationUsageResponse>(path);
    },

    async downloadSuperAdminCommunicationUsageReport(
      params?: PlatformCommunicationUsageQuery
    ): Promise<Blob> {
      const qs = new URLSearchParams();
      if (params?.tenant_id != null && String(params.tenant_id).trim()) {
        qs.set('tenant_id', String(params.tenant_id));
      }
      if (params?.granularity) qs.set('granularity', params.granularity);
      if (params?.from_date) qs.set('from_date', params.from_date);
      if (params?.to_date) qs.set('to_date', params.to_date);
      if (params?.channel) qs.set('channel', params.channel);
      const query = qs.toString();
      const path = query
        ? `${SUPER_ADMIN_PATHS.communicationUsageReport}?${query}`
        : SUPER_ADMIN_PATHS.communicationUsageReport;
      const response = await fetch(`${client.baseUrl}${path}`, {
        method: 'GET',
        headers: client.authHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Platform communication usage report download failed: ${response.status}`);
      }
      return response.blob();
    },
  };
}
